import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { FormGroup, FormLabel } from 'react-bootstrap';
import { Button, Toggle } from '@storable/canaveral-components';
import Map, { MAX_ZOOM, MIN_ZOOM } from '../Map';
import Select from '../../../../modules/react/components/stateful/Select';
import FormFieldLabel from '../../../../admin/components/FormFieldLabel';
import ColorPicker from '../../../../src/web/components/form/inputs/ColorPicker/ColorPicker';
import styles from './styles.scss';

const MAP_STYLES = [
  { value: 'mapbox://styles/mapbox/streets-v12', label: 'Streets' },
  { value: 'mapbox://styles/mapbox/light-v11', label: 'Light Grey' },
];

const propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  disabled: PropTypes.bool,
  initialCenter: PropTypes.shape({
    latitude: PropTypes.number.isRequired,
    longitude: PropTypes.number.isRequired,
  }),
  initialZoom: PropTypes.number,
  onPan: PropTypes.func,
  onZoom: PropTypes.func,
  multiFacility: PropTypes.bool,
  onMapStyleUpdate: PropTypes.func,
  mapStyle: PropTypes.string,

  colors: PropTypes.shape({
    pin: PropTypes.string,
    cluster: PropTypes.string,
  }),

  onColorsChange: PropTypes.func,
  allowConfiguration: PropTypes.bool,
};

const defaultProps = {
  children: undefined,
  disabled: undefined,
  initialCenter: undefined,
  initialZoom: undefined,
  onPan: undefined,
  onZoom: undefined,
  multiFacility: false,
  onMapStyleUpdate: undefined,
  mapStyle: 'mapbox://styles/mapbox/streets-v12',
  onColorsChange: undefined,
  colors: {
    pin: 'primary',
    cluster: 'secondary',
  },
  allowConfiguration: true,
};

const MapEditor = ({
  children,
  disabled,
  initialCenter,
  initialZoom,
  clusterRadius,
  enableClustering,
  onClusterToggle,
  onClusterRadiusChange,
  onPan,
  onZoom,
  onMapStyleUpdate,
  mapStyle,
  colors,
  onColorsChange,
  multiFacility,
  allowConfiguration,
  ...rest
}) => {
  const [center] = useState(initialCenter);
  const [zoom, setZoom] = useState(initialZoom);
  const [pannable, setPannable] = useState(false);
  const mapRef = useRef();

  useEffect(() => {
    if (onZoom) {
      onZoom(zoom);
    }
  }, [zoom]);

  const panContext = pannable ? 'positive' : 'neutral';
  const panIcon = pannable ? 'hand-rock' : 'hand-paper';
  const panLabel = pannable ? 'Disable pan' : 'Enable pan';

  const onZoomIn = () => {
    mapRef.current?.zoomIn();
  };

  const onZoomOut = () => {
    mapRef.current?.zoomOut();
  };

  const onTogglePan = () => setPannable(!pannable);

  const onCenterChange = (newCenter) => {
    if (!pannable) {
      return;
    }

    if (onPan) {
      onPan(newCenter);
    }
  };

  return (
    <React.Fragment>
      <div className={styles.controlBar}>
        {multiFacility ? (
          <FormGroup>
            <Toggle
              checked={enableClustering}
              onChange={(ev) => {
                onClusterToggle(ev.target.checked);
              }}
            />
            <FormLabel className={styles.headerStyle}>
              <span>Enable Pin Clustering</span>
            </FormLabel>
          </FormGroup>
        ) : (
          <div style={{ width: 10, height: 10 }} />
        )}
        <div className={styles.controls}>
          <Button.Group>
            <Button
              context="neutral"
              disabled={
                disabled || mapRef.current?.getCurrentZoom() === MAX_ZOOM
              }
              icon="search-plus"
              label="Zoom in"
              onClick={onZoomIn}
            />
            <Button
              context="neutral"
              disabled={
                disabled || mapRef.current?.getCurrentZoom() === MIN_ZOOM
              }
              icon="search-minus"
              label="Zoom out"
              onClick={onZoomOut}
            />
          </Button.Group>
          <Button
            context={panContext}
            disabled={disabled}
            icon={panIcon}
            label={panLabel}
            onClick={onTogglePan}
          />
        </div>
      </div>
      <div style={{ width: '100%', height: 400 }}>
        <Map
          {...rest}
          ref={mapRef}
          center={center}
          height={400}
          width="100%"
          pannable={pannable}
          rotatable={false}
          onCenterChange={onCenterChange}
          onZoom={setZoom}
          zoom={zoom}
          disableControls
          zoomable={false}
          clusterRadius={clusterRadius}
          enableClustering={enableClustering}
          mapStyle={mapStyle}
          colors={colors}
          allowPopup={false}
          useDefaultZoomInAbsence={!multiFacility}
          source="mapEditor"
        >
          {children}
        </Map>
      </div>

      {allowConfiguration && (
        <FormGroup>
          <FormFieldLabel htmlFor="mapPinColor" title="Map Pin Color" />
          <ColorPicker
            allowCustom={false}
            mapPinColorPicker
            input={{
              name: 'mapPinColor',
              value: colors.pin,
              onChange: (newValue) => {
                onColorsChange({ ...colors, pin: newValue });
              },
            }}
          />
        </FormGroup>
      )}

      {allowConfiguration && enableClustering && (
        <FormGroup>
          <FormFieldLabel htmlFor="mapClusterColor" title="Cluster Color" />
          <ColorPicker
            allowCustom={false}
            mapPinColorPicker
            input={{
              name: 'mapClusterColor',
              value: colors.cluster,
              onChange: (newValue) => {
                onColorsChange({ ...colors, cluster: newValue });
              },
            }}
          />
        </FormGroup>
      )}

      {allowConfiguration && (
        <FormGroup>
          <FormFieldLabel htmlFor="mapStyle" title="Map Style" />
          <Select
            canClear={false}
            name="mapStyle"
            defaultValue={MAP_STYLES[0].value}
            value={mapStyle}
            options={MAP_STYLES}
            onChange={(ev) => {
              onMapStyleUpdate(ev.value);
            }}
          />
        </FormGroup>
      )}
    </React.Fragment>
  );
};

MapEditor.propTypes = propTypes;
MapEditor.defaultProps = defaultProps;

export default MapEditor;
